import { createContext, useContext, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CalendarDays, MessageCircle, CheckCircle2 } from "lucide-react";
import { CALENDLY } from "./data";

const WA_NUMBER = "918696938848";
const BookCallContext = createContext(null);
export const useBookCall = () => useContext(BookCallContext);

const inputCls =
  "w-full rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--bg))] px-4 py-3.5 text-base text-[hsl(var(--fg))] placeholder:text-[hsl(var(--muted))] outline-none transition-colors duration-300 focus:border-[hsl(var(--primary))]";

const OVERLAY_INITIAL = { opacity: 0 };
const OVERLAY_ANIMATE = { opacity: 1 };
const MODAL_INITIAL = { opacity: 0, y: 30, scale: 0.97 };
const MODAL_ANIMATE = { opacity: 1, y: 0, scale: 1 };
const MODAL_EXIT = { opacity: 0, y: 20, scale: 0.97 };
const MODAL_TRANSITION = { type: "spring", stiffness: 300, damping: 26 };

const validate = (form) => {
  if (!form.name.trim()) return "Please fill your name.";
  const digits = form.phone.replace(/\D/g, "");
  if (digits.length < 8 || digits.length > 15)
    return "Please enter a valid WhatsApp number (with country code if outside India).";
  return "";
};

const buildWhatsAppText = (form) =>
  [
    "New Discovery Call booking request!",
    `Name: ${form.name}`,
    `WhatsApp: ${form.phone}`,
    form.note && `About: ${form.note}`,
    "(I am picking a Calendly slot now)",
  ]
    .filter(Boolean)
    .join("\n");

const Step1Form = ({ form, set, error, submit, nameRef }) => (
  <form onSubmit={submit}>
    <p className="font-head text-2xl font-semibold">Book your Discovery Call</p>
    <p className="mt-1 text-sm text-[hsl(var(--muted))]">
      Share your details so we can confirm your booking on WhatsApp — then pick a slot that works for you.
    </p>
    <div className="mt-6 grid gap-4">
      <input
        ref={nameRef}
        data-testid="book-call-name"
        value={form.name}
        onChange={set("name")}
        placeholder="Your name *"
        className={inputCls}
      />
      <input
        data-testid="book-call-phone"
        type="tel"
        inputMode="tel"
        value={form.phone}
        onChange={set("phone")}
        placeholder="WhatsApp number *"
        className={inputCls}
      />
      <input
        data-testid="book-call-note"
        value={form.note}
        onChange={set("note")}
        placeholder="What's the project about? (optional)"
        className={inputCls}
      />
    </div>
    {error && (
      <p data-testid="book-call-error" className="mt-3 text-sm text-red-400">
        {error}
      </p>
    )}
    <button
      type="submit"
      data-testid="book-call-submit"
      className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-[hsl(var(--primary))] px-8 py-4 text-base font-semibold text-[hsl(var(--primary-fg))] transition-transform duration-300 hover:scale-[1.02]"
    >
      <MessageCircle size={17} /> Confirm on WhatsApp
    </button>
    <p className="mt-3 text-center text-xs text-[hsl(var(--muted))]">
      Step 1 of 2 — next you pick your time slot
    </p>
  </form>
);

const Step2Panel = ({ form, close }) => (
  <div data-testid="book-call-step2" className="text-center">
    <CheckCircle2 size={44} className="mx-auto text-[hsl(var(--primary))]" />
    <p className="mt-4 font-head text-2xl font-semibold">Almost done, {form.name.split(" ")[0]}!</p>
    <p className="mt-2 text-sm text-[hsl(var(--muted))]">
      Send the WhatsApp message that just opened, then pick your 30-min slot below. You&apos;ll get a Google Meet invite by email.
    </p>
    <a
      href={`${CALENDLY}?name=${encodeURIComponent(form.name)}`}
      target="_blank"
      rel="noreferrer"
      data-testid="book-call-calendly-btn"
      className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-[hsl(var(--primary))] px-8 py-4 text-base font-semibold text-[hsl(var(--primary-fg))] transition-transform duration-300 hover:scale-[1.02]"
    >
      <CalendarDays size={17} /> Pick your time slot
    </a>
    <button
      onClick={close}
      data-testid="book-call-done-btn"
      className="mt-3 text-sm text-[hsl(var(--muted))] underline-offset-4 hover:underline"
    >
      Done, close this
    </button>
  </div>
);

export const BookCallProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ name: "", phone: "", note: "" });
  const [error, setError] = useState("");
  const nameRef = useRef(null);

  const openModal = () => {
    setStep(1);
    setError("");
    setForm({ name: "", phone: "", note: "" });
    setOpen(true);
  };
  const close = () => setOpen(false);
  const set = (k) => (e) => {
    const v = e.target.value;
    setForm((prev) => ({ ...prev, [k]: v }));
    setError("");
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    const t = setTimeout(() => nameRef.current?.focus(), 100);
    return () => {
      window.removeEventListener("keydown", onKey);
      clearTimeout(t);
    };
  }, [open]);

  const submit = (e) => {
    e.preventDefault();
    const err = validate(form);
    if (err) {
      setError(err);
      return;
    }
    setError("");
    window.open(
      `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(buildWhatsAppText(form))}`,
      "_blank"
    );
    setStep(2);
  };

  return (
    <BookCallContext.Provider value={{ openBookCall: openModal }}>
      {children}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={OVERLAY_INITIAL}
            animate={OVERLAY_ANIMATE}
            exit={OVERLAY_INITIAL}
            data-testid="book-call-modal-overlay"
            className="fixed inset-0 z-[150] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
            onClick={close}
          >
            <motion.div
              initial={MODAL_INITIAL}
              animate={MODAL_ANIMATE}
              exit={MODAL_EXIT}
              transition={MODAL_TRANSITION}
              data-testid="book-call-modal"
              role="dialog"
              aria-modal="true"
              aria-label="Book your Discovery Call"
              className="relative w-full max-w-md rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--bg-soft))] p-7 md:p-8"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={close}
                data-testid="book-call-close-btn"
                aria-label="Close"
                className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full border border-[hsl(var(--border))] text-[hsl(var(--muted))] transition-colors hover:text-[hsl(var(--fg))]"
              >
                <X size={16} />
              </button>
              {step === 1 ? (
                <Step1Form form={form} set={set} error={error} submit={submit} nameRef={nameRef} />
              ) : (
                <Step2Panel form={form} close={close} />
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </BookCallContext.Provider>
  );
};
